import * as React from 'react';
import type { AddedAnalyticsProps } from '@helpcenter/analytics-components/src/with-create-fire-ui-analytics';
import { isUserPickerSupportEnabled } from '@helpcenter/feature-flags';
import {
	ThemedSelect,
	ThemedAsyncSelect,
	ThemedAsyncCreatableSelect,
} from 'view/common/themed-select';
import { withControlledFormComponentValue } from 'view/common/with-controlled-form-component-value';
import type { Option, SelectProps } from '@atlaskit/select';
import { componentWithCondition } from '@atlassian/help-center-common-util/feature-flags-utils';
import type { FieldOption } from '../with-controlled-value-single-select';
import { withControlledValueSingleSelect } from '../with-controlled-value-single-select';
import type { SelectFieldFormProps } from '../with-field-base';
import { withFieldBase } from '../with-field-base';

const FieldSingleSelect = withFieldBase(ThemedSelect);
const FieldAsyncSingleSelect = withFieldBase(ThemedAsyncSelect);
const FieldAsyncCreatableSingleSelect = withFieldBase(ThemedAsyncCreatableSelect);

interface FormAsyncCreatableSingleSelectBaseProps {
	showNewLabelForCreatedValues?: boolean;
}

type PropsWithField = Omit<SelectProps<FieldOption<string>>, 'value'> &
	Omit<SelectFieldFormProps, 'value'> &
	AddedAnalyticsProps;

class FormAsyncCreatableSingleSelectBase extends React.PureComponent<
	FormAsyncCreatableSingleSelectBaseProps &
		SelectFieldFormProps &
		SelectProps<FieldOption<string>> &
		AddedAnalyticsProps
> {
	onCreateOption = (input: string) => {
		const { onChange, showNewLabelForCreatedValues } = this.props;
		const newOption = {
			value: input,
			label: input,
			isNew: showNewLabelForCreatedValues,
		};

		onChange?.(newOption, { action: 'create-option', option: newOption });
	};

	render() {
		return <FieldAsyncCreatableSingleSelect {...this.props} onCreateOption={this.onCreateOption} />;
	}
}

const ControlledFieldAsyncSingleSelect = withControlledFormComponentValue<Option<string> | null>(
	(option: Option<string>) => option,
)((props: PropsWithField) => <FieldAsyncSingleSelect {...props} isMulti={false} isClearable />);

const ControlledFormAsyncCreatableSingleSelectBase =
	withControlledFormComponentValue<Option<string> | null>((option: Option<string>) => option)(
		(props: PropsWithField) => (
			<FormAsyncCreatableSingleSelectBase {...props} isMulti={false} isClearable />
		),
	);

const FormSingleSelect = withControlledFormComponentValue<Option<string> | null>(
	(option: Option<string>) => option,
)(FieldSingleSelect);

const FormAsyncSingleSelect = componentWithCondition(
	() => isUserPickerSupportEnabled(),
	ControlledFieldAsyncSingleSelect,
	withControlledValueSingleSelect<string>(FieldAsyncSingleSelect),
);

const FormAsyncCreatableSingleSelect = componentWithCondition(
	() => isUserPickerSupportEnabled(),
	ControlledFormAsyncCreatableSingleSelectBase,
	withControlledValueSingleSelect<string>(FormAsyncCreatableSingleSelectBase),
);

export {
	FormSingleSelect,
	FormAsyncSingleSelect,
	FormAsyncCreatableSingleSelect,
	FieldSingleSelect,
};
